import { z } from "zod";

// Personal Details Schema (with file uploads for aadhar and pan)
export const personalDetailsSchema = z
    .object({
        fullName: z
            .string({ required_error: "Full Name is required." })
            .min(3, { message: "Full Name must be at least 3 characters" })
            .max(100, { message: "Full Name must be at most 100 characters" }),
        dob: z.preprocess(
            (val) => {
                if (typeof val === "string" || val instanceof Date) return new Date(val);
            },
            z.date({ required_error: "Date of Birth is required." })
                .refine((d) => !isNaN(d.getTime()), { message: "Invalid date format" })
        ),
        gender: z.enum(["Male", "Female", "Other"], { required_error: "Gender is required." }),
        phone1: z
            .string({ required_error: "Phone number is required." })
            .length(10, { message: "Phone number must be exactly 10 digits." })
            .regex(/^[6-9]\d{9}$/, { message: "Invalid Indian mobile number" }),
        phone2: z.preprocess(
            (val) => val === "" ? undefined : val,
            z.string()
                .length(10, { message: "Phone number must be exactly 10 digits." })
                .regex(/^[6-9]\d{9}$/, { message: "Invalid Indian mobile number" })
                .optional()
        ),
        email: z
            .string({ required_error: "Email is required." })
            .email({ message: "Invalid email format." }),
        monthlyShipments: z
            .coerce.string({ required_error: "Monthly shipments field is required." })
            .min(1, { message: "Monthly shipments cannot be empty." }),
        address: z.preprocess(
            (val) => (val === undefined ? {} : val),
            z.object({
                line1: z.string({ required_error: "Address is required." }).max(200, { message: "Address must be at most 200 characters" }),
                city: z.string({ required_error: "City is required." }).max(100, { message: "City must be at most 100 characters" }),
                state: z.string({ required_error: "State is required." }).max(100, { message: "State must be at most 100 characters" }),
                pincode: z.coerce.string({ required_error: "Pincode is required." }).regex(/^\d{6}$/, { message: "Pincode must be exactly 6 digits" }),
            })
        ),
        panNumber: z
            .string({ required_error: "PAN number is required." })
            .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, {
                message: "Invalid PAN number format (e.g., ABCDE1234F).",
            }),
        aadharNumber: z
            .string({ required_error: "Aadhar number is required." })
            .length(12, { message: "Aadhar number must be exactly 12 digits." })
            .regex(/^\d{12}$/, { message: "Aadhar number must contain only digits." }),
    })

// GST Details Schema (with file uploads for firm GST, PAN, and logo)
export const gstDetailsSchema = z
    .object({
        firmName: z
            .string({ required_error: "Firm name is required" })
            .min(3, { message: "Firm name must be at least 3 characters" })
            .max(100, { message: "Firm name must be at most 100 characters" }),
        businessType: z.enum(
            [
                "Sole Proprietorship",
                "Partnership Firm",
                "Limited Liability Partnership (LLP)",
                "Private Limited Company",
                "Public Limited Company",
                "One Person Company (OPC)",
                "Cooperative Society",
                "Joint Hindu Family Business",
                "Section 8 Company",
                "Trusts",
                "Non-Banking Financial Companies (NBFCs)",
                "Franchise Business",
            ], { required_error: "Business type is required" }
        ),
        phone: z
            .string({ required_error: "Business phone is required" })
            .regex(/^[6-9]\d{9}$/, { message: "Invalid business phone number" }),
        address: z.preprocess(
            (val) => (val === undefined ? {} : val),
            z.object({
                line1: z.string({ required_error: "Address is required" }).max(200, { message: "Address must be at most 200 characters" }),
                city: z.string({ required_error: "City is required" }).max(100, { message: "City must be at most 100 characters" }),
                state: z.string({ required_error: "State is required" }).max(100, { message: "State must be at most 100 characters" }),
                pincode: z.coerce.string({ required_error: "Pincode is required" }).regex(/^\d{6}$/, { message: "Pincode must be exactly 6 digits" }),
            })
        ),
        gstNumber: z
            .string({ required_error: "GST Number is required" })
            .nonempty("GST Number is required")
            .regex(
                /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                "Invalid GST Number format"
            ),
        firmPanNumber: z
            .string({ required_error: "Firm PAN Number is required" })
            .nonempty("Firm PAN Number is required")
            .regex(
                /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                "Invalid Firm PAN Number format"
            ),
    })

// Bank Details Schema (with file upload for cancelled cheque)
export const bankDetailsSchema = z
    .object({
        accountHolderName: z
            .string({ required_error: "Account holder name is required" })
            .min(3, { message: "Account holder name must be at least 3 characters" })
            .max(100, { message: "Account holder name must be at most 100 characters" }),
        bankName: z
            .string({ required_error: "Bank name is required" })
            .min(3, { message: "Bank name must be at least 3 characters" })
            .max(100, { message: "Bank name must be at most 100 characters" }),
        accountNumber: z
            .string({ required_error: "Account number is required" })
            .regex(/^\d{9,18}$/, { message: "Invalid account number" }),
        confirmAccountNumber: z
            .string({ required_error: "Confirm account number is required" })
            .nonempty("Confirm account number is required"),
        accountType: z.enum(["Saving", "Current"], { required_error: "Account type is required" }),
        ifscCode: z
            .string({ required_error: "IFSC code is required" })
            .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, { message: "Invalid IFSC code format" }),
    })
    .superRefine((data, ctx) => {
        if (data.accountNumber !== data.confirmAccountNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Account numbers do not match",
                path: ["confirmAccountNumber"],
            });
        }
    })
    .transform(({ confirmAccountNumber, ...rest }) => rest)



// Overall KYC schema that combines the above and validates the file uploads
export const kycSchema = z
    .object({
        personalDetails: personalDetailsSchema,
        businessDetails: gstDetailsSchema.optional(),
        bankDetails: bankDetailsSchema,
        kycDocuments: z.object({
            aadhar: z.union([
                z.string({ required_error: "Aadhar card photo URL is required" }),
                z.instanceof(File)
            ]),
            pan: z.union([
                z.string({ required_error: "PAN card photo URL is required" }),
                z.instanceof(File)
            ]),
            cancelledCheque: z.union([
                z.string({ required_error: "Cancelled cheque photo URL is required" }),
                z.instanceof(File)
            ]),
            firmPan: z.union([
                z.string({ message: "Firm PAN card photo URL is required" }),
                z.instanceof(File)
            ]).optional(),
            firmGST: z.union([
                z.string({ message: "Firm GST certificate photo URL is required" }),
                z.instanceof(File)
            ]).optional(),
            firmLOGO: z.union([
                z.string({ message: "Firm logo photo URL is required" }),
                z.instanceof(File)
            ]).optional(),
        }),
        kycStatus: z.string().optional(),
    })
    .strict();